"use client";

import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { DestinationsUi } from "@/lib/content/pages/destinations";
import type { Destination } from "@/lib/destinations/types";

type Step = {
  id: string;
  eyebrow: string;
  title: string;
  description: string;
  imageSrc?: string;
};

type Props = {
  destination: Destination | null;
  onClose: () => void;
  locale: "fr" | "en";
  ui: DestinationsUi;
  contactHref: string;
};

/**
 * Pop-up "Sticky Steps" — détail d'une ville :
 * colonne gauche = étapes (eyebrow + titre + texte) qui défilent avec fondu
 * selon la position de scroll ; colonne droite = image ovale sticky qui
 * cross-fade vers le visuel de l'étape active.
 */
export default function StickyStepsModal({
  destination,
  onClose,
  locale,
  ui,
  contactHref,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [active, setActive] = useState(0);

  // Verrouille le scroll de la page derrière la pop-up
  useEffect(() => {
    if (!destination) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [destination]);

  // Fermeture au clavier
  useEffect(() => {
    if (!destination) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [destination, onClose]);

  // Étape active = celle dont le centre est le plus proche du centre du conteneur
  const onScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const mid = container.getBoundingClientRect().top + container.clientHeight / 2;
    let best = 0;
    let bestDist = Infinity;
    stepRefs.current.forEach((el, i) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      const d = Math.abs(r.top + r.height / 2 - mid);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    setActive((a) => (a === best ? a : best));
  }, []);

  // Reset au changement de ville
  useEffect(() => {
    setActive(0);
    scrollRef.current?.scrollTo({ top: 0 });
  }, [destination?.id]);

  if (!destination) {
    return <AnimatePresence />;
  }

  const hotelLabel = ui.hotelLabel;
  const activityLabel = ui.activityLabel;

  // Étape 0 : intro ville, puis hôtels, puis activités.
  // Images : popupImageSrc (portrait dédié à l'ovale) en priorité, repli sur imageSrc.
  const steps: Step[] = [
    {
      id: "__intro",
      eyebrow: destination.country[locale],
      title: destination.city,
      description: destination.intro[locale],
      imageSrc:
        destination.heroImageSrc ??
        destination.hotels[0]?.popupImageSrc ??
        destination.hotels[0]?.imageSrc ??
        destination.activities[0]?.popupImageSrc ??
        destination.activities[0]?.imageSrc,
    },
    ...destination.hotels.map((h, i) => ({
      id: h.id,
      eyebrow: `${hotelLabel} ${String(i + 1).padStart(2, "0")} · ${h.category[locale]}`,
      title: h.name,
      description: h.description[locale],
      imageSrc: h.popupImageSrc ?? h.imageSrc,
    })),
    ...destination.activities.map((a, i) => ({
      id: a.id,
      eyebrow: `${activityLabel} ${String(i + 1).padStart(2, "0")} · ${a.category[locale]}`,
      title: a.name[locale],
      description: a.description[locale],
      imageSrc: a.popupImageSrc ?? a.imageSrc,
    })),
  ];

  return (
    <AnimatePresence>
      <motion.div
        key={destination.id}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
        role="dialog"
        aria-modal="true"
        aria-label={`${destination.city} — ${locale === "fr" ? "détails" : "details"}`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Fenêtre */}
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.98 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 h-[86dvh] w-full max-w-6xl overflow-hidden rounded-3xl border border-white/10 bg-[#0b0b0d]"
        >
          {/* Fermer */}
          <button
            type="button"
            onClick={onClose}
            aria-label={locale === "fr" ? "Fermer" : "Close"}
            className="absolute right-5 top-5 z-30 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-black/40 text-white/70 backdrop-blur-md transition hover:bg-white/10 hover:text-white"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M6 6l12 12M6 18L18 6" strokeLinecap="round" />
            </svg>
          </button>

          {/* Conteneur scrollable — pilote les steps */}
          <div
            ref={scrollRef}
            onScroll={onScroll}
            className="h-full overflow-y-auto"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* ── Colonne gauche : les étapes ── */}
              <div className="px-8 sm:px-12 lg:px-16">
                {steps.map((step, i) => (
                  <div
                    key={step.id}
                    ref={(el) => {
                      stepRefs.current[i] = el;
                    }}
                    className={[
                      "flex min-h-[72vh] flex-col justify-center py-12",
                      "transition-opacity duration-500",
                      i === active ? "opacity-100" : "opacity-25",
                    ].join(" ")}
                  >
                    <p className="text-[10px] tracking-[0.32em] uppercase text-white/45 font-body">
                      {step.eyebrow}
                    </p>
                    <h3 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] text-white">
                      {step.title}
                    </h3>
                    <p className="mt-6 max-w-md text-sm sm:text-base leading-relaxed text-white/60 font-body font-light">
                      {step.description}
                    </p>

                    {/* Image mobile (l'ovale sticky est masqué sous lg) */}
                    {step.imageSrc && (
                      <div className="relative mt-8 h-52 w-full overflow-hidden rounded-2xl lg:hidden">
                        <Image
                          src={step.imageSrc}
                          alt={step.title}
                          fill
                          className="object-cover"
                          sizes="90vw"
                        />
                      </div>
                    )}
                  </div>
                ))}

                {/* CTA final */}
                <div className="pb-16">
                  <Link
                    href={contactHref}
                    className="inline-flex items-center gap-3 rounded-full bg-white px-8 py-3.5 text-xs tracking-[0.2em] uppercase text-black transition-opacity hover:opacity-90"
                  >
                    {ui.requestFor.replace("{city}", destination.city)}
                    <span>→</span>
                  </Link>
                </div>
              </div>

              {/* ── Colonne droite : image ovale sticky ── */}
              <div className="hidden lg:block">
                <div className="sticky top-0 flex h-[86vh] items-center justify-center">
                  {/* Grand carré aux bords arrondis */}
                  <div className="relative aspect-square w-[82%] max-w-[500px] overflow-hidden rounded-[2rem]">
                    {steps.map((step, i) =>
                      step.imageSrc ? (
                        <Image
                          key={step.id}
                          src={step.imageSrc}
                          alt={step.title}
                          fill
                          className={[
                            "object-cover transition-opacity duration-700 ease-out",
                            i === active ? "opacity-100" : "opacity-0",
                          ].join(" ")}
                          sizes="40vw"
                          priority={i === 0}
                        />
                      ) : (
                        <div
                          key={step.id}
                          className={[
                            "absolute inset-0 flex items-center justify-center bg-gradient-to-br from-white/12 via-white/[0.05] to-transparent transition-opacity duration-700",
                            i === active ? "opacity-100" : "opacity-0",
                          ].join(" ")}
                        >
                          <span className="text-[9px] tracking-[0.3em] uppercase text-white/25 font-body">
                            {step.eyebrow}
                          </span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
